import { Overlay } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  Component,
  Inject,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { StationsAutoCompleteMapMarkerComponent } from 'src/app/modules/route-planner/components/route-planner-form/forms/search-station-form/stations-auto-complete/stations-auto-complete-map/stations-auto-complete-map-marker/stations-auto-complete-map-marker.component';
import { BaseMap } from 'src/app/modules/route-planner/components/shared/map/base-map.component';
import { MapComponent } from 'src/app/modules/route-planner/components/shared/map/map.component';
import { MAP_ICONS } from 'src/app/modules/route-planner/constants/map-icons.constants';
import { StopFullData } from 'src/app/modules/route-planner/interfaces/route-planning/service/@schedule-service.interface';
import { FavoritesService } from 'src/app/modules/route-planner/services/favorites/favorites.service';
import { MapService } from 'src/app/modules/route-planner/services/map/map.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';

@Component({
    selector: 'app-search-station-deatiled-result-map',
    templateUrl: './search-station-deatiled-result-map.component.html',
    styleUrls: ['./search-station-deatiled-result-map.component.scss'],
    providers: [MapService],
    standalone: false
})
export class SearchStationDeatiledResultMapComponent
  extends BaseMap<StopFullData>
  implements AfterViewInit, OnInit {
  @ViewChild(MapComponent)
  override mapComponent?: MapComponent;

  @Input() stop: StopFullData;
  private stationMarker: H.map.Marker;
  private stationDomMarker: H.map.DomMarker;

  constructor(
    @Inject(DATA_INJECTION_TOKEN)
    public readonly data: { stop: StopFullData, showPickStationButton: boolean },
    favoritesService: FavoritesService,
    resizeService: ResizeService,
    overlay: Overlay,
    mapService: MapService,
    routePlanningService: RoutePlanningService,
    private readonly viewContainerRef: ViewContainerRef
  ) {
    super(
      favoritesService,
      resizeService,
      overlay,
      mapService,
      routePlanningService
    );
  }

  override ngOnInit(): void {
    if (!this.stop && this.data.stop) {
      this.stop = this.data.stop;
    }
    this.routePlanningService.setMapService(this.mapService);
  }

  override ngAfterViewInit(): void {
    this.mapService.setViewContainerRef(this.viewContainerRef);

    if (!this.mapService.map && !!this.mapComponent) {
      this.mapService.initializeMap(
        this.mapComponent.mapDiv,
        {
          focusOnUserLocation: false,
          controls: {
            myLocation: true,
            zoom: true,
          },
        }
      );
    } else if (this.mapService.map && !!this.mapComponent) {
      // Reusing existing map instance
      this._mapInitialized = true;
    }

    // Only subscribe if map is not already initialized
    if (!this._mapInitialized) {
      this.mapService.$mapInitialized.subscribe((map) => {
        this._mapInitialized = true;
      if (!this.stop || this.stop.lat == null || this.stop.lng == null) {
        console.error('Stop data is missing or invalid:', this.stop);
        return;
      }

      const lat = Number(this.stop.lat);
      const lng = Number(this.stop.lng);
      const coordinates = new H.geo.Point(lat, lng);
      
      // Create the station marker with high zIndex to ensure visibility
      this.stationMarker = this.mapService.addMarker(
        coordinates,
        MAP_ICONS.selected_station(),
        {
          ...this.stop,
          alwaysVisible: true,
        }
      );
      
      this.stationMarker.setZIndex(1000);
      
      const icon = this.createStationMarker();

      this.stationMarker.addEventListener('tap', (ev) => {
        ev.stopPropagation();
        
        if (this.stationDomMarker) {
          this.mapService.removeObject(this.stationDomMarker);
          this.stationDomMarker = null;
          return;
        }
        
        const newIcon = this.createStationMarker();
        this.stationDomMarker = this.mapService.addDomMarker(
          coordinates,
          newIcon,
          this.stop
        );
        if (this.stationDomMarker) {
          this.stationDomMarker.setZIndex(1001);
        }
      });

      // Add the DOM marker (info popup) immediately on load
      this.stationDomMarker = this.mapService.addDomMarker(
        coordinates,
        icon,
        this.stop
      );
      if (this.stationDomMarker) {
        this.stationDomMarker.setZIndex(1001); // Even higher z-index for the DOM marker
      }

      // Set up map view change listener to ensure marker visibility
      this.mapService.map.addEventListener('mapviewchangeend', () => {
        // Make sure our markers are always visible
        if (this.stationMarker) {
          this.stationMarker.setVisibility(true);
        }
        if (this.stationDomMarker) {
          this.stationDomMarker.setVisibility(true);
        }
      });

      this.mapService.updateMapLocation(coordinates, 18, true);
      });
    } else {
      // Map already initialized, set up markers directly
      if (this.stop && this.stop.lat != null && this.stop.lng != null) {
        const lat = Number(this.stop.lat);
        const lng = Number(this.stop.lng);
        const coordinates = new H.geo.Point(lat, lng);
        
        this.stationMarker = this.mapService.addMarker(
          coordinates,
          MAP_ICONS.selected_station(),
          {
            ...this.stop,
            alwaysVisible: true,
          }
        );
        this.stationMarker.setZIndex(1000);
        
        const icon = this.createStationMarker();
        this.stationDomMarker = this.mapService.addDomMarker(
          coordinates,
          icon,
          this.stop
        );
        if (this.stationDomMarker) {
          this.stationDomMarker.setZIndex(1001);
        }
        
        this.mapService.updateMapLocation(coordinates, 18, true);
      }
    }
  }

  createStationMarker() : H.map.DomIcon {
    const container = this.mapService.createMapObject<
      StationsAutoCompleteMapMarkerComponent,
      any
    >(StationsAutoCompleteMapMarkerComponent, { stop: this.stop, showPickStationButton: this.data.showPickStationButton });
    const icon = new H.map.DomIcon(container, {
      onAttach: (element) => {


        const closeButton = element.getElementsByClassName('close-station-data').item(0);
        if (closeButton) {
          closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (this.stationDomMarker) {
              this.mapService.removeObject(this.stationDomMarker);
              this.stationDomMarker = null;
            }
            this.stationMarker.setIcon(MAP_ICONS.station());
          });
        }
      }
    });
    return icon;
  }
}
